import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { NotificationsState, Notification } from "@/types/notification.types";
import { notificationsApi } from "@/services/api/endpoints/notifications.api";

// Initial state
const initialState: NotificationsState = {
  notifications: [],
  unreadCount: 0,
  soundEnabled: true,
  preferences: {
    email: true,
    sms: false,
    push: true,
    volumeSurgeAlerts: true,
    priceAlerts: true,
    marketUpdates: true,
  },
  loading: false,
  error: null,
};

// Note: API endpoints would be created in services/api/endpoints/notifications.api.ts
// For now, using placeholder async thunks

// Async Thunks
export const fetchNotifications = createAsyncThunk(
  "notifications/fetchNotifications",
  async (
    params:
      | { page?: number; unreadOnly?: boolean; symbol?: string }
      | undefined,
    { rejectWithValue },
  ) => {
    try {
      const data = await notificationsApi.getNotifications(params);
      return data.alerts;
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to fetch notifications");
    }
  },
);

export const fetchUnreadCount = createAsyncThunk(
  "notifications/fetchUnreadCount",
  async (_, { rejectWithValue }) => {
    try {
      const count = await notificationsApi.getUnreadCount();
      return count;
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to fetch unread count");
    }
  },
);

export const markAsRead = createAsyncThunk(
  "notifications/markAsRead",
  async (id: number | string, { rejectWithValue }) => {
    try {
      await notificationsApi.markAsRead(id);
      return id;
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to mark notification as read");
    }
  },
);

export const markAllAsRead = createAsyncThunk(
  "notifications/markAllAsRead",
  async (_, { rejectWithValue }) => {
    try {
      await notificationsApi.markAllAsRead();
      return null;
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to mark all as read");
    }
  },
);

// Slice
const notificationsSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    addNotification: (state, action: PayloadAction<Notification>) => {
      state.notifications.unshift(action.payload);
      if (!action.payload.read) {
        state.unreadCount += 1;
      }
    },
    toggleSound: (state) => {
      state.soundEnabled = !state.soundEnabled;
    },
    clearAllNotifications: (state) => {
      state.notifications = [];
      state.unreadCount = 0;
    },
    updatePreferences: (
      state,
      action: PayloadAction<Partial<NotificationsState["preferences"]>>,
    ) => {
      state.preferences = { ...state.preferences, ...action.payload };
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Notifications
    builder
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.notifications = action.payload;
        // We probably also want to update unread count if we fetched all
        // state.unreadCount = action.payload.filter(n => !n.read).length;
        state.error = null;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch Unread Count
    builder.addCase(fetchUnreadCount.fulfilled, (state, action) => {
      state.unreadCount = action.payload;
    });

    // Mark as Read
    builder.addCase(markAsRead.fulfilled, (state, action) => {
      const id = action.payload;
      // Handle both string and number IDs potentially
      const notification = state.notifications.find(
        (n) => n.id === id || String(n.id) === String(id),
      );
      if (notification && !notification.read) {
        notification.read = true;
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    });

    // Mark All as Read
    builder.addCase(markAllAsRead.fulfilled, (state) => {
      state.notifications.forEach((n) => (n.read = true));
      state.unreadCount = 0;
    });
  },
});

// Actions
export const {
  addNotification,
  toggleSound,
  clearAllNotifications,
  updatePreferences,
  clearError,
} = notificationsSlice.actions;

// Selectors
export const selectNotifications = (state: {
  notifications: NotificationsState;
}) => state.notifications.notifications;
export const selectUnreadCount = (state: {
  notifications: NotificationsState;
}) => state.notifications.unreadCount;
export const selectSoundEnabled = (state: {
  notifications: NotificationsState;
}) => state.notifications.soundEnabled;
export const selectNotificationPreferences = (state: {
  notifications: NotificationsState;
}) => state.notifications.preferences;
export const selectNotificationsLoading = (state: {
  notifications: NotificationsState;
}) => state.notifications.loading;
export const selectNotificationsError = (state: {
  notifications: NotificationsState;
}) => state.notifications.error;

export default notificationsSlice.reducer;
